import { createContext, useContext } from 'react'
import type { TranscriptBlock } from '../../transport/api'
import { Icon } from '../../ui/Icon'
import type { NewBookmark } from './useBookmarks'
import './bookmarks.css'

/**
 * The bookmark under a message in Clean View (owner, 2026-10-31: 每一個 Message Block 下面有一個書簽的按鈕, 當使用者按下這個
 * 按鈕之後, 就可以把該 Message Block 收進書簽裡面).
 *
 * The transcript view provides what a button needs — which block this is (its address in the transcript) and whether it
 * is kept — through context, so a message (Msg, memoised and kept still on purpose) does not re-render when a bookmark
 * changes: only the buttons do. Where nothing provides it — a friend's shared agent, the reply still being written, the
 * message just sent — a block has no address yet, and there is no button.
 */
export type BookmarkCtx = {
  /** The block's address, `file:index`, or null when this view cannot place it. */
  addressOf: (b: TranscriptBlock) => { key: string; file: string; index: number } | null
  /** The bookmark kept at that address: its id (a `pending:` id while the machine has not answered), or null. */
  keptAt: (key: string) => string | null
  add: (b: NewBookmark) => void
  remove: (id: string) => void
}

export const BookmarkContext = createContext<BookmarkCtx | null>(null)

export function BookmarkButton({ b, who, kind, text }: { b: TranscriptBlock; who: string; kind: NewBookmark['kind']; text: string }) {
  const ctx = useContext(BookmarkContext)
  const at = ctx?.addressOf(b) ?? null
  if (!ctx || !at) return null
  const id = ctx.keptAt(at.key)
  const pending = !!id && id.startsWith('pending:')
  return (
    <div className="bm-row">
      <button
        type="button"
        className={`bm-btn${id ? ' on' : ''}`}
        data-bm-index={at.index}
        aria-pressed={!!id}
        aria-label={id ? 'Remove this message from bookmarks' : 'Bookmark this message'}
        title={id ? 'Bookmarked — tap to remove' : 'Bookmark this message'}
        disabled={pending}
        onClick={() => {
          if (id) ctx.remove(id)
          else ctx.add({ file: at.file, index: at.index, ts: b.ts ?? null, who, kind, text: text.trim() || '(an attachment)' })
        }}
      >
        <Icon name={id ? 'bookmark_filled' : 'bookmark'} size={16} />
      </button>
    </div>
  )
}
